import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Plus, Edit2, Trash2, X, Check, ExternalLink,
  ToggleLeft, ToggleRight
} from 'lucide-react';
import AppHeader from '../components/AppHeader';

const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminWellnessPartnersPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    url: '',
    logo_url: '',
    is_active: true
  });

  useEffect(() => {
    if (user?.role !== 'admin' && user?.base_role !== 'admin') {
      navigate('/');
      return;
    }
    fetchPartners();
  }, [user, navigate]);

  const fetchPartners = async () => {
    try {
      const res = await fetch(`${API_URL}/api/mvp/wellness/partners?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPartners(data || []);
    } catch (err) {
      console.error('Failed to fetch partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        // Update
        await fetch(`${API_URL}/api/mvp/wellness/partners/${editingPartner.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(form)
        });
      } else {
        // Create
        await fetch(`${API_URL}/api/mvp/wellness/partners`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(form)
        });
      }
      setShowModal(false);
      setEditingPartner(null);
      setForm({ name: '', description: '', url: '', logo_url: '', is_active: true });
      fetchPartners();
    } catch (err) {
      alert('Failed to save partner');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setForm({
      name: partner.name,
      description: partner.description,
      url: partner.url,
      logo_url: partner.logo_url || '',
      is_active: partner.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await fetch(`${API_URL}/api/mvp/wellness/partners/${partnerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchPartners();
    } catch (err) {
      alert('Failed to delete partner');
    }
  };

  const handleToggleActive = async (partner) => {
    try {
      await fetch(`${API_URL}/api/mvp/wellness/partners/${partner.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !partner.is_active })
      });
      fetchPartners();
    } catch (err) {
      alert('Failed to update partner');
    }
  };

  const activeCount = partners.filter(p => p.is_active).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title="Wellness Partners" />
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Sparkles className="text-purple-600" size={28} />
            <h1 className="text-2xl font-bold text-gray-800">Wellness Partners</h1>
          </div>
          <button
            onClick={() => {
              setEditingPartner(null);
              setForm({ name: '', description: '', url: '', logo_url: '', is_active: true });
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Plus size={18} />
            Add Partner
          </button>
        </div>

        {/* Info */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
          <p className="text-purple-800 text-sm">
            <strong>Tip:</strong> Keep max 3 active partners for a clean care-seeker experience.
            Currently {activeCount} active.
          </p>
        </div>

        {/* Partners List */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : partners.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Sparkles className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No wellness partners yet. Add your first one!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {partners.map(partner => (
              <div 
                key={partner.id}
                className={`bg-white rounded-xl p-4 shadow-sm border-2 ${
                  partner.is_active ? 'border-purple-200' : 'border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-start gap-4">
                  {partner.logo_url ? (
                    <img 
                      src={partner.logo_url} 
                      alt={partner.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
                      <Sparkles className="text-white" size={28} />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-800">{partner.name}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        partner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {partner.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{partner.description}</p>
                    <a 
                      href={partner.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                    >
                      {partner.url}
                      <ExternalLink size={12} />
                    </a>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(partner)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      title={partner.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {partner.is_active ? (
                        <ToggleRight className="text-green-600" size={24} />
                      ) : (
                        <ToggleLeft className="text-gray-400" size={24} />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(partner)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-blue-600"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(partner.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-red-600"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  {editingPartner ? 'Edit Partner' : 'Add Wellness Partner'}
                </h2>
                <button onClick={() => setShowModal(false)}><X size={20} /></button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., MindfulU"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    required
                    value={form.description}
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                    placeholder="Brief description of the wellness service"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                  <input
                    type="url"
                    required
                    value={form.url}
                    onChange={(e) => setForm({...form, url: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (optional)</label>
                  <input
                    type="url"
                    value={form.logo_url}
                    onChange={(e) => setForm({...form, logo_url: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://..."
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={form.is_active}
                    onChange={(e) => setForm({...form, is_active: e.target.checked})}
                    className="rounded"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Active (visible to care-seekers)</label>
                </div>
                
                <button
                  type="submit"
                  className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingPartner ? 'Update Partner' : 'Add Partner'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
